import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Star, MapPin, ShieldCheck, FileText, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { StatCard, Card, Spinner, EmptyState } from "../components/dashboard/DashboardShell";
import OrdersTable from "../components/dashboard/OrdersTable";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { whyInspecar, testimonials, IMAGES } from "../mock";

const WHY_ICONS = [ShieldCheck, FileText, CreditCard, MapPin];

export default function UserDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: d, isLoading } = useQuery({ queryKey: ["user-dashboard"], queryFn: () => api.get("/users/me/dashboard").then((r) => r.data) });
  if (isLoading || !d) return <Spinner />;
  const stats = [
    { label: "Total Pesanan", value: d.stats.total, icon: "clipboard", color: "text-blue-600 bg-blue-50", testId: "stat-total" },
    { label: "Sedang Berlangsung", value: d.stats.berlangsung, icon: "loader", color: "text-violet-600 bg-violet-50", testId: "stat-berlangsung" },
    { label: "Selesai", value: d.stats.selesai, icon: "check", color: "text-emerald-600 bg-emerald-50", testId: "stat-selesai" },
    { label: "Dibatalkan", value: d.stats.dibatalkan, icon: "x", color: "text-red-500 bg-red-50", testId: "stat-dibatalkan" },
  ];
  return (
    <div data-testid="user-dashboard">
      <h1 className="text-2xl font-extrabold text-slate-900">Dashboard Saya</h1>
      <p className="text-sm text-slate-500 mt-1">Halo, <span className="font-bold text-slate-700">{user.name}</span> · Kelola pesanan inspeksi Anda dengan mudah dan aman.</p>

      <div className="relative mt-5 rounded-2xl overflow-hidden h-44 sm:h-52">
        <img src={IMAGES.ctaCouple} alt="Inspeksi profesional" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/50 to-transparent" />
        <div className="relative z-10 p-6 sm:p-8 max-w-md">
          <h2 className="text-white text-xl sm:text-2xl font-extrabold leading-tight">Keputusan Lebih Baik<br />Dimulai dari Inspeksi Profesional</h2>
          <p className="text-slate-300 text-xs sm:text-sm mt-2">Mobil yang aman, perjalanan yang lebih tenang.</p>
          <Button onClick={() => navigate("/pengguna/cari")} data-testid="cta-order-btn" className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-10 px-5 text-sm shadow-lg shadow-blue-900/40">Pesan Inspeksi Sekarang <ArrowRight size={15} className="ml-1.5" /></Button>
        </div>
        <p className="absolute top-5 right-6 font-hand text-2xl text-white rotate-2 hidden sm:block">Cek Dulu,<br />Baru Gas!</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">{stats.map((s) => <StatCard key={s.label} {...s} />)}</div>

      <div className="grid xl:grid-cols-3 gap-4 mt-4">
        <Card title="Pesanan Terbaru" link="Lihat Semua" onLink={() => navigate("/pengguna/pesanan")} className="xl:col-span-2">
          <OrdersTable orders={d.recent_orders} compact emptyTitle="Belum ada pesanan" />
        </Card>
        <Card title="Inspector Favorit" link="Lihat Semua" onLink={() => navigate("/pengguna/favorit")}>
          {!d.favorites.length ? <EmptyState icon="heart" title="Belum ada favorit" /> : (
            <div className="space-y-4">
              {d.favorites.map((f) => (
                <div key={f.user_id} className="flex items-center gap-3">
                  <img src={f.picture} alt={f.name} className="w-11 h-11 rounded-full object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-slate-900 truncate">{f.name}</p>
                    <p className="text-[11px] text-amber-500 font-bold flex items-center gap-1"><Star size={10} fill="#f59e0b" /> {f.rating_avg} <span className="text-slate-400 font-medium">({f.rating_count} ulasan)</span></p>
                    <p className="text-[11px] text-slate-400 flex items-center gap-1"><MapPin size={10} /> {f.city}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/pengguna/inspector/${f.user_id}`)} data-testid={`fav-book-${f.user_id}`} className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs font-bold rounded-lg h-8">Pesan</Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
        <Card title="Kenapa Memilih Inspecar?">
          <div className="grid grid-cols-2 gap-4">
            {whyInspecar.map((w, i) => { const Icon = WHY_ICONS[i]; return (
              <div key={w.title} className="text-center py-2"><span className="inline-flex w-11 h-11 rounded-2xl bg-blue-50 items-center justify-center mb-2"><Icon size={20} className="text-blue-600" /></span><p className="text-[11px] font-bold text-slate-700 leading-tight">{w.title}<br />{w.sub}</p></div>
            ); })}
          </div>
        </Card>
        <Card title="Apa Kata Mereka?">
          <div className="text-center">
            <img src={testimonials[1].avatar} alt={testimonials[1].name} className="w-12 h-12 rounded-full object-cover mx-auto" />
            <p className="text-xs text-slate-600 leading-relaxed mt-3">"{testimonials[1].text}"</p>
            <p className="text-amber-400 text-xs mt-2">★★★★★</p>
            <p className="text-[11px] font-bold text-slate-900 mt-1">{testimonials[1].name}</p>
          </div>
        </Card>
        <div className="relative rounded-2xl overflow-hidden md:col-span-2 xl:col-span-1 min-h-[220px]">
          <img src={IMAGES.promoCar} alt="Mobil aman" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent" />
          <div className="absolute bottom-0 p-5">
            <p className="text-white text-lg font-extrabold leading-tight">Mobil Aman,<br />Hidup Lebih Tenang</p>
            <Button onClick={() => navigate("/pengguna/cari")} className="mt-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-10 px-5 text-sm">Pesan Inspeksi</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
